import i18n from './i18n'

/**
 * Formats file size in bytes to human readable format using locale-specific formatting
 * @param sizeInBytes - The file size in bytes
 * @param language - The language code to use for localization (defaults to current language)
 * @returns Locale-formatted file size string with appropriate unit (byte, kilobyte, megabyte, gigabyte, terabyte)
 */
export const getFormattedFileSize = (
  sizeInBytes?: number,
  language = i18n.language,
): string => {
  if (!sizeInBytes) return ''

  // Define conversion factors
  const KB = 1024
  const MB = KB * 1024
  const GB = MB * 1024
  const TB = GB * 1024

  // Choose the appropriate unit and get localized unit name
  let size: number
  let unit: string

  if (sizeInBytes >= TB) {
    // For file sizes larger than 1 TB
    size = sizeInBytes / TB
    unit = 'terabyte'
    size = parseFloat(size.toFixed(2))
  } else if (sizeInBytes >= GB) {
    // For file sizes larger than 1 GB
    size = sizeInBytes / GB
    unit = 'gigabyte'
    size = parseFloat(size.toFixed(2))
  } else if (sizeInBytes >= MB) {
    // For file sizes larger than 1 MB
    size = sizeInBytes / MB
    unit = 'megabyte'
    size = parseFloat(size.toFixed(2))
  } else if (sizeInBytes >= KB) {
    // For file sizes larger than 1 KB
    size = Math.round(sizeInBytes / KB)
    unit = 'kilobyte'
  } else {
    // For very small files
    size = Math.round(sizeInBytes)
    unit = 'byte'
  }

  // Format number according to the locale
  const formattedSize = size.toLocaleString(language, {
    style: 'unit',
    unit: unit,
  })

  return formattedSize
}

/**
 * Formats a number according to the specified locale and fraction digits
 * @param value - The number to format
 * @param fractionDigits - Number of decimal places (optional). If undefined, no specific decimal places are set
 * @param language - The language code to use for localization (defaults to current language)
 * @returns Locale-formatted number string
 */
export const formatQuantity = (
  value: number,
  fractionDigits?: number,
  language = i18n.language,
): string => {
  if (value === null || value === undefined || isNaN(value)) return ''

  if (fractionDigits !== undefined) {
    return value.toLocaleString(language, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })
  }

  return value.toLocaleString(language)
}
